<script setup lang="ts">
/**
 * Inline SVG icon registry, ported from the design bundle's
 * `partner-app/components/Icons.jsx`. Geometry mirrors Carbon icon
 * stroke style (2px, square caps) and is meant as a lightweight
 * stand-in until `@carbon/icons-vue` is wired up.
 *
 * Usage:
 *   <CdsIcon name="search" />
 *   <CdsIcon name="add" :size="20" />
 */
import { computed } from 'vue'

export type CdsIconName =
  | 'menu'
  | 'close'
  | 'search'
  | 'add'
  | 'filter'
  | 'bell'
  | 'settings'
  | 'chevron-down'
  | 'chevron-right'
  | 'arrow-right'
  | 'overflow'
  | 'dashboard'
  | 'users'
  | 'inbox'
  | 'euro'
  | 'doc'
  | 'check-circle'
  | 'alert-triangle'
  | 'alert-circle'
  | 'help'
  | 'calendar'
  | 'tag'
  | 'percent'
  | 'calculator'
  | 'chart'
  | 'upload'
  | 'download'
  | 'compass'
  | 'refresh'
  | 'history'

const props = withDefaults(
  defineProps<{
    name: CdsIconName
    size?: number
    title?: string
  }>(),
  { size: 16, title: '' },
)

const ariaHidden = computed(() => (props.title ? 'false' : 'true'))
</script>

<template>
  <svg
    :width="size"
    :height="size"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    stroke-width="2"
    stroke-linecap="square"
    stroke-linejoin="miter"
    :aria-hidden="ariaHidden"
    :aria-label="title || undefined"
    :role="title ? 'img' : undefined"
  >
    <title v-if="title">{{ title }}</title>

    <template v-if="name === 'menu'">
      <line x1="3" y1="6" x2="21" y2="6" />
      <line x1="3" y1="12" x2="21" y2="12" />
      <line x1="3" y1="18" x2="21" y2="18" />
    </template>

    <template v-else-if="name === 'close'">
      <line x1="5" y1="5" x2="19" y2="19" />
      <line x1="19" y1="5" x2="5" y2="19" />
    </template>

    <template v-else-if="name === 'search'">
      <circle cx="10.5" cy="10.5" r="6.5" />
      <line x1="20" y1="20" x2="15.5" y2="15.5" />
    </template>

    <template v-else-if="name === 'add'">
      <line x1="12" y1="4" x2="12" y2="20" />
      <line x1="4" y1="12" x2="20" y2="12" />
    </template>

    <template v-else-if="name === 'filter'">
      <line x1="4" y1="6" x2="20" y2="6" />
      <line x1="7" y1="12" x2="17" y2="12" />
      <line x1="10" y1="18" x2="14" y2="18" />
    </template>

    <template v-else-if="name === 'bell'">
      <path d="M6 8a6 6 0 0112 0v5l1.5 3h-15L6 13z" />
      <path d="M10 19a2 2 0 004 0" />
    </template>

    <template v-else-if="name === 'settings'">
      <circle cx="12" cy="12" r="3" />
      <path
        d="M19.4 15a1.7 1.7 0 00.34 1.87l.06.06a2 2 0 11-2.83 2.83l-.06-.06a1.7 1.7 0 00-1.87-.34 1.7 1.7 0 00-1.04 1.56V21a2 2 0 11-4 0v-.09A1.7 1.7 0 009 19.4a1.7 1.7 0 00-1.87.34l-.06.06a2 2 0 11-2.83-2.83l.06-.06A1.7 1.7 0 004.6 15a1.7 1.7 0 00-1.56-1.04H3a2 2 0 110-4h.09A1.7 1.7 0 004.6 9a1.7 1.7 0 00-.34-1.87l-.06-.06a2 2 0 112.83-2.83l.06.06A1.7 1.7 0 009 4.6a1.7 1.7 0 001.04-1.56V3a2 2 0 114 0v.09A1.7 1.7 0 0015 4.6a1.7 1.7 0 001.87-.34l.06-.06a2 2 0 112.83 2.83l-.06.06A1.7 1.7 0 0019.4 9c.31.06.6.18.85.34"
      />
    </template>

    <template v-else-if="name === 'chevron-down'">
      <polyline points="6 9 12 15 18 9" />
    </template>

    <template v-else-if="name === 'chevron-right'">
      <polyline points="9 6 15 12 9 18" />
    </template>

    <template v-else-if="name === 'arrow-right'">
      <line x1="5" y1="12" x2="19" y2="12" />
      <polyline points="12 5 19 12 12 19" />
    </template>

    <template v-else-if="name === 'overflow'">
      <circle cx="12" cy="5" r="1.2" fill="currentColor" />
      <circle cx="12" cy="12" r="1.2" fill="currentColor" />
      <circle cx="12" cy="19" r="1.2" fill="currentColor" />
    </template>

    <template v-else-if="name === 'dashboard'">
      <rect x="3" y="3" width="8" height="10" />
      <rect x="13" y="3" width="8" height="6" />
      <rect x="13" y="11" width="8" height="10" />
      <rect x="3" y="15" width="8" height="6" />
    </template>

    <template v-else-if="name === 'users'">
      <circle cx="9" cy="8" r="4" />
      <path d="M2 21a7 7 0 0114 0" />
      <circle cx="17" cy="9" r="3" />
      <path d="M22 19a5 5 0 00-7-3.6" />
    </template>

    <template v-else-if="name === 'inbox'">
      <polyline points="22 12 16 12 14 15 10 15 8 12 2 12" />
      <path d="M5 6h14l3 6v6a2 2 0 01-2 2H4a2 2 0 01-2-2v-6z" />
    </template>

    <template v-else-if="name === 'euro'">
      <path d="M19 6a7 7 0 100 12" />
      <line x1="3" y1="10" x2="14" y2="10" />
      <line x1="3" y1="14" x2="14" y2="14" />
    </template>

    <template v-else-if="name === 'doc'">
      <path d="M14 3H6a2 2 0 00-2 2v14a2 2 0 002 2h12a2 2 0 002-2V9z" />
      <polyline points="14 3 14 9 20 9" />
      <line x1="8" y1="13" x2="16" y2="13" />
      <line x1="8" y1="17" x2="13" y2="17" />
    </template>

    <template v-else-if="name === 'check-circle'">
      <circle cx="12" cy="12" r="9" />
      <polyline points="8 12 11 15 16 9" />
    </template>

    <template v-else-if="name === 'alert-triangle'">
      <path d="M12 3l10 18H2z" />
      <line x1="12" y1="10" x2="12" y2="14" />
      <circle cx="12" cy="17" r="0.5" fill="currentColor" />
    </template>

    <template v-else-if="name === 'alert-circle'">
      <circle cx="12" cy="12" r="9" />
      <line x1="12" y1="8" x2="12" y2="13" />
      <circle cx="12" cy="16" r="0.5" fill="currentColor" />
    </template>

    <template v-else-if="name === 'help'">
      <circle cx="12" cy="12" r="9" />
      <path d="M9.5 9a2.5 2.5 0 015 0c0 1.5-2.5 2-2.5 4" />
      <circle cx="12" cy="17" r="0.5" fill="currentColor" />
    </template>

    <template v-else-if="name === 'calendar'">
      <rect x="3" y="5" width="18" height="16" rx="0" />
      <line x1="3" y1="9" x2="21" y2="9" />
      <line x1="8" y1="3" x2="8" y2="7" />
      <line x1="16" y1="3" x2="16" y2="7" />
    </template>

    <template v-else-if="name === 'tag'">
      <path d="M3 12V4h8l10 10-8 8z" />
      <circle cx="7.5" cy="7.5" r="1.2" fill="currentColor" />
    </template>

    <template v-else-if="name === 'percent'">
      <line x1="5" y1="19" x2="19" y2="5" />
      <circle cx="7" cy="7" r="2.5" />
      <circle cx="17" cy="17" r="2.5" />
    </template>

    <template v-else-if="name === 'calculator'">
      <rect x="5" y="3" width="14" height="18" />
      <rect x="8" y="6" width="8" height="3" />
      <line x1="9" y1="13" x2="9" y2="13" />
      <line x1="12" y1="13" x2="12" y2="13" />
      <line x1="15" y1="13" x2="15" y2="13" />
      <line x1="9" y1="17" x2="9" y2="17" />
      <line x1="12" y1="17" x2="12" y2="17" />
      <line x1="15" y1="17" x2="15" y2="17" />
    </template>

    <template v-else-if="name === 'chart'">
      <line x1="4" y1="20" x2="20" y2="20" />
      <rect x="6" y="12" width="3" height="6" />
      <rect x="11" y="8" width="3" height="10" />
      <rect x="16" y="14" width="3" height="4" />
    </template>

    <template v-else-if="name === 'upload'">
      <path d="M12 4v12" />
      <polyline points="7 9 12 4 17 9" />
      <line x1="4" y1="20" x2="20" y2="20" />
    </template>

    <template v-else-if="name === 'download'">
      <path d="M12 4v12" />
      <polyline points="7 11 12 16 17 11" />
      <line x1="4" y1="20" x2="20" y2="20" />
    </template>

    <template v-else-if="name === 'compass'">
      <circle cx="12" cy="12" r="9" />
      <polygon points="15.5,8.5 13,13.5 8.5,15.5 11,10.5" fill="currentColor" stroke="none" />
    </template>

    <template v-else-if="name === 'refresh'">
      <polyline points="23 4 23 10 17 10" />
      <polyline points="1 20 1 14 7 14" />
      <path d="M3.5 9a9 9 0 0114.85-3.36L23 10" />
      <path d="M20.5 15a9 9 0 01-14.85 3.36L1 14" />
    </template>

    <template v-else-if="name === 'history'">
      <circle cx="12" cy="12" r="9" />
      <polyline points="12 7 12 12 16 14" />
    </template>
  </svg>
</template>
